<template>
    <div
        class="three-d-navbar"
        :style="{ 'width': navbarWidth + 'px'}"
    >
        <!-- Navigation bar -->
        <v-container
            fluid
            class="three-d-navbar-content pa-2"
        >
            <v-row
                class="text-center"
                no-gutters
            >
                <v-slide-y-reverse-transition>
                    <!-- Navigation bar opened -->
                    <v-col
                        v-show="!options.minimize"
                        cols="12"
                    >
                        <div class="d-flex align-center justify-center">
                            <!-- Compass -->
                            <div
                                v-if="useCompass && options.gui.compass && isWidgetReady"
                                class="pa-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['compass']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <Compass
                                                id="gtm-threedcontroller-cta-compass"
                                                :widgetType="widgetType"
                                                :compassData="options.compass"
                                            />
                                        </div>
                                    </template>
                                    <span>
                                        {{ options.gui.compass.text }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- /Compass -->

                            <!-- Autorotate button -->
                            <div
                                class="pa-2"
                                :class="{ 'ml-2': useCompass && options.gui.compass }"
                                v-if="options.gui.autoRotate"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['autorotate']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            id="gtm-threedcontroller-cta-autorotate"
                                            v-bind="attrs"
                                            v-on="on"
                                            small
                                            fab
                                            :class="{ 'active': $props.options.gui.autoRotate.active }"
                                            @click.prevent="toggleAutoRotate();"
                                        >
                                            <v-icon>
                                                icon-video
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>
                                        {{ options.gui.autoRotate.text }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- /Autorotate button -->

                            <!-- Move group buttons -->
                            <div
                                v-if="options.gui.moveLeft && options.gui.moveRight && (!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm)"
                                class="pa-2 ml-2"
                            >
                                <v-btn-toggle rounded>
                                    <!-- Move left button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['moveLeft']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-moveleft"
                                                v-bind="attrs"
                                                v-on="on"
                                                :disabled="isRotating"
                                                @click.prevent="moveLeft()"
                                                @mousedown="startMoveLeft"
                                                @mouseleave="stopMoveLeft"
                                                @mouseup="stopMoveLeft"
                                                @touchstart="startMoveLeft"
                                                @touchend="stopMoveLeft"
                                                @touchcancel="stopMoveLeft"
                                            >
                                                <v-icon>
                                                    icon-arrow-left-alternative
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.moveLeft.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Move left button -->

                                    <!-- Mouse move button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['moveWidget']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-mousemode"
                                                v-bind="attrs"
                                                v-on="on"
                                                ref="mouseMode"
                                                :class="{ 'active': rotateMode}"
                                                @click.prevent="toggleMouseMode()"
                                            >
                                                <v-icon>
                                                    icon-stump
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ $t('threeDController.tooltips.move') }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Mouse move button -->

                                    <!-- Move right button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['moveRight']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-moveright"
                                                v-bind="attrs"
                                                v-on="on"
                                                :disabled="isRotating"
                                                @click.prevent="moveRight()"
                                                @mousedown="startMoveRight"
                                                @mouseleave="stopMoveRight"
                                                @mouseup="stopMoveRight"
                                                @touchstart="startMoveRight"
                                                @touchend="stopMoveRight"
                                                @touchcancel="stopMoveRight"
                                            >
                                                <v-icon>
                                                    icon-arrow-right-alternative
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.moveRight.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Move right button -->
                                </v-btn-toggle>
                            </div>

                            <!-- Mouse move button -->
                            <div
                                v-else-if="options.gui.move"
                                class="pa-2 ml-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['moveWidget']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            id="gtm-threedcontroller-cta-mousemode"
                                            v-bind="attrs"
                                            v-on="on"
                                            ref="mouseMode"
                                            small
                                            fab
                                            :class="{ 'active': rotateMode}"
                                            @click.prevent="toggleMouseMode()"
                                        >
                                            <v-icon>
                                                icon-stump
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>
                                        {{ $t('threeDController.tooltips.move') }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- /Mouse move button -->

                            <div
                                class="pa-2 ml-2"
                                v-if="options.gui.moveUp && options.gui.moveDown"
                            >
                                <v-btn-toggle rounded>
                                    <!-- /Move up button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['moveUp']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-moveup"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.prevent="moveUp()"
                                            >
                                                <v-icon>
                                                    icon-arrow-up-alternative
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.moveUp.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Move up button -->

                                    <!-- Move down button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['moveDown']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-movedown"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.prevent="moveDown()"
                                            >
                                                <v-icon>
                                                    icon-arrow-down-alternative
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.moveDown.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Move down button -->
                                </v-btn-toggle>
                            </div>
                            <!-- /Move group buttons -->

                            <!-- Zoom group buttons -->
                            <div
                                v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm && (options.gui.zoomPlus && options.gui.zoomMinus)"
                                class="pa-2 ml-2"
                            >
                                <v-btn-toggle rounded>
                                    <!-- Zoom in button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['zoomIn']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-zoomin"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.prevent="zoomIn();"
                                                @mousedown="startZoomIn"
                                                @mouseleave="stopZoomIn"
                                                @mouseup="stopZoomIn"
                                                @touchstart="startZoomIn"
                                                @touchend="stopZoomIn"
                                                @touchcancel="stopZoomIn"
                                            >
                                                <v-icon>
                                                    icon-zoom-in
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.zoomPlus.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Zoom in button -->

                                    <!-- Zoom out button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['zoomOut']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-zoomout"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.prevent="zoomOut();"
                                                @mousedown="startZoomOut"
                                                @mouseleave="stopZoomOut"
                                                @mouseup="stopZoomOut"
                                                @touchstart="startZoomOut"
                                                @touchend="stopZoomOut"
                                                @touchcancel="stopZoomOut"
                                            >
                                                <v-icon>
                                                    icon-zoom-out
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.zoomMinus.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Zoom out button -->

                                    <!-- Fullscreen button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['fullscreen']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-fullscreen"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.prevent="toggleFullscreen()"
                                            >
                                                <v-icon v-if="!fullscreen">
                                                    icon-full-screen
                                                </v-icon>
                                                <v-icon v-else>
                                                    icon-reduce
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.fullscreen.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /Fullscreen button -->
                                </v-btn-toggle>
                            </div>
                            <!-- /Zoom group buttons -->

                            <!-- Fullscreen button -->
                            <div
                                v-if="$vuetify.breakpoint.sm && isIframe && options.gui.fullscreen"
                                class="pa-2 ml-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['fullscreen']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            id="gtm-threedcontroller-cta-fullscreen"
                                            v-bind="attrs"
                                            v-on="on"
                                            small
                                            fab
                                            @click.prevent="toggleFullscreen()"
                                        >
                                            <v-icon v-if="!fullscreen">
                                                icon-full-screen
                                            </v-icon>
                                            <v-icon v-else>
                                                icon-reduce
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>
                                        {{ options.gui.fullscreen.text }}
                                    </span>
                                </v-tooltip>
                            </div>
                                <!-- /Fullscreen button -->

                            <!-- Help popup button -->
                            <div
                                v-if="options.gui.help"
                                class="pa-2 ml-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['helpPopup']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            id="gtm-threedcontroller-cta-help"
                                            v-bind="attrs"
                                            v-on="on"
                                            :class="{ 'active': popupHelpOpen }"
                                            small
                                            fab
                                            ref="help-button"
                                            @click.prevent="toggleHelp()"
                                            v-click-outside="close"
                                        >
                                            <v-icon>
                                                icon-bulb
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>
                                        {{ options.gui.help.text }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- /Help popup button -->

                            <!-- Resolution buttons group -->
                            <div
                                v-if="options.gui.qualityFullHD && options.gui.quality4k"
                                class="pa-2 ml-2"
                            >
                                <v-btn-toggle rounded>
                                    <!-- HD button-->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['fullHD']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-switchHD"
                                                v-bind="attrs"
                                                v-on="on"
                                                class="resolution-button"
                                                :class="{'active': resolution === '720p'}"
                                                @click.prevent="resolution !== '720p' ? switchResolution('720p') : null"
                                            >
                                                HD
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.qualityFullHD.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /HD button -->

                                    <!-- 4K button -->
                                    <v-tooltip 
                                        content-class="top"
                                        top
                                        v-model="showTooltip['4k']"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                id="gtm-threedcontroller-cta-switch4K"
                                                v-bind="attrs"
                                                v-on="on"
                                                class="resolution-button"
                                                :class="{'active': resolution === '4k'}"
                                                @click.prevent="resolution !== '4k' ? switchResolution('4k') : null"
                                            >
                                                4K
                                            </v-btn>
                                        </template>
                                        <span>
                                            {{ options.gui.quality4k.text }}
                                        </span>
                                    </v-tooltip>
                                    <!-- /4K button -->
                                </v-btn-toggle>
                            </div>
                            <!-- /Resolution buttons group -->

                            <!-- Language settings button-->
                            <div
                                v-if="!isReviewUse"
                                v-show="$vuetify.breakpoint.lgAndUp || (!options.gui.qualityFullHD && !options.gui.quality4k && $vuetify.breakpoint.mdAndDown)"
                                class="pa-2 ml-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['language']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            id="gtm-threedcontroller-cta-language"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <languageSettings :language="language"/>
                                        </div>
                                    </template>
                                    <span>
                                        {{ $t('threeDController.tooltips.languages') }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- /Language settings button-->

                            <!-- Hide menu button-->
                            <div
                                v-if="options.gui.hidden"
                                v-show="screenWidth > 375"
                                class="pa-2 ml-2"
                            >
                                <v-tooltip 
                                    content-class="top"
                                    top
                                    v-model="showTooltip['hideNavbar']"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            id="gtm-threedcontroller-cta-hidenav"
                                            v-bind="attrs"
                                            v-on="on"
                                            small
                                            fab
                                            @click.prevent="toggleMinimize()"
                                        >
                                            <v-icon>
                                                icon-hidden
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>
                                        {{ options.gui.hidden.text }}
                                    </span>
                                </v-tooltip>
                            </div>
                            <!-- Hide menu button-->
                        </div>
                    </v-col>
                    <!-- /Navigation bar opened -->
                </v-slide-y-reverse-transition>

                <v-slide-y-transition>
                    <!-- Navigation bar closed -->
                    <v-col
                        v-if="options.gui.opener"
                        v-show="this.$props.options.minimize"
                        cols="12"
                    >
                        <!-- Open menu button-->
                        <div class="d-flex align-center justify-center pa-6">
                            <v-tooltip 
                                content-class="top"
                                top
                                v-model="showTooltip['openNavbar']"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        id="gtm-threedcontroller-cta-shownav"
                                        v-bind="attrs"
                                        v-on="on"
                                        small
                                        fab
                                        @click.prevent="toggleMinimize()"
                                    >
                                        <v-icon>
                                            icon-open
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>
                                    {{ options.gui.opener.text }}
                                </span>
                            </v-tooltip>
                        </div>
                        <!-- Open menu button-->
                    </v-col>
                    <!-- /Navigation bar closed -->
                </v-slide-y-transition>
            </v-row>
        </v-container>
        <!-- /Navigation bar -->

        <!-- Help dialog for mobile -->
        <div
            class="text-center"
            v-if="useHelpDialog"
        >
            <v-dialog
                v-model="openHelpDialog"
                width="300"
                overlay-opacity="0.15"
            >
                <v-card outlined>
                    <!-- content of HelpPopup-->
                    <HelpPopup @close-help-popup="closeHelpPopup"/>
                </v-card>
            </v-dialog>
        </div>
        <!-- /Help dialog for mobile -->

        <!-- Help popup for desktop and tablet -->
        <v-slide-y-reverse-transition v-else>
            <v-card
                v-show="popupHelpOpen"
                ref="help-popup"
                class="alternativePopup"
                :style="{
                    'position': 'absolute',
                    'left': popupPosition ? popupPosition.left + 'px' : null,
                    'bottom': popupPosition ? popupPosition.bottom + 'px' : null
                }"
                outlined
                width="300"
            >
                <!-- content of HelpPopup-->
                <HelpPopup @close-help-popup="closeHelpPopup"/>
            </v-card>
        </v-slide-y-reverse-transition>
        <!-- /Help popup for desktop and tablet -->
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    import Compass from "@/components/threeDController/components/Compass";
    import HelpPopup from "@/components/threeDController/components/HelpPopup";
    import LanguageSettings from "@/components/threeDController/components/LanguageSettings";

    export default {
        name: 'ThreeDController',
        components: {
            Compass,
            HelpPopup,
            LanguageSettings
        },
        props: [
            'options',
            'widget',
            'targetElement',
            'canBeEditable',
            'useCompass',
            'widgetType'
        ],
        data: () => ({
            popupHelpOpen: false,
            openHelpDialog: false,
            fullscreen: false,
            rotateMode: false,
            popupPosition: null,
            currentViewAngle: null,
            currentFrames: null,
            isRotating: false,
            sidebarOpened: false,
            navbarWidth: null,
            hasClicked: false,
            screenWidth: null,
            isKeyDown: false,
            showTooltip: {
                'compass' : false,
                'autorotate': false,
                'moveLeft': false,
                'moveRight': false,
                'moveWidget': false,
                'moveUp': false,
                'moveDown': false,
                'zoomIn': false,
                'zoomOut': false,
                'fullscreen': false,
                'helpPopup': false,
                'fullHD': false,
                '4k': false,
                'hideNavbar': false,
                'openNavbar': false,
                'language': false,
            },
            tooltipDuration: 2000,
            tooltipTimeout: null,
            disabledWidgetPopup: ['map'],
            isWidgetReady: false
        }),
        computed: {
            ...mapGetters([
                'isLocalstorageAvailable',
                'language',
                'isReviewUse',
                'resolution'
            ]),
            useHelpDialog() {
                return this.$vuetify.breakpoint.xs || (this.$vuetify.breakpoint.sm && !this.isPortraitOrientation);
            },
            isPortrait() {
                const orientation = window.screen.orientation.type;
                // Check orientation type
                if (orientation === "portrait-primary" || orientation === "portrait-secondary")
                    return true;
                else if (orientation === "landscape-primary" || orientation === "landscape-secondary")
                    return false;
                else
                    return false;
            },
            isIframe() {
                return window.self !== window.top;
            },
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.floorsKeyboardControls, true);
            document.removeEventListener('keyup', this.resetKeyPress, true);
        },
        mounted() {
            this.screenWidth = window.innerWidth;

            this.$set(this.$props.options, 'minimize', false);
            this.checkNav();

            EventBus.$on('updateSidebarStatus', sidebarStatus => this.sidebarOpened = sidebarStatus);

            EventBus.$on('widgetReady', widget => {
                if (widget.keyboardRotationControls)
                    widget.keyboardRotationControls();

                if (this.$props.widgetType === 'cutaway')
                    if (widget.keyboardRotationControls)
                        widget.keyboardUpDownControls();

                this.isWidgetReady = true;
            });

            if (this.$props.widgetType === 'cutaway') {
                document.addEventListener('keydown', this.floorsKeyboardControls, true);
                document.addEventListener('keyup', this.resetKeyPress, true);
            }

            EventBus.$on('closeHelpPopup', () => {
                this.hasClicked = true;
                this.close();
            });

            let sidebarStatus;
            EventBus.$emit('getSidebarSatus', status => {
                sidebarStatus = status;
            });
            this.sidebarOpened = sidebarStatus;

            this.setNavigationBarWidth();

            if (!this.disabledWidgetPopup.includes(this.widgetType))
                this.displayInfoPopup();
            
            window.addEventListener('resize', this.updatePopupPosition);
            window.addEventListener('resize', this.setNavigationBarWidth);
        },
        methods: {
            ...mapActions([
                'setResolution',
            ]),
            floorsKeyboardControls(event) {
                var keyState = {};
                switch (event['keyCode']) {
                    case !this.isKeyDown && 38:
                        this.widget.rotateUp();
                        this.updateFloor('up');
                        this.isKeyDown = !this.isKeyDown;
                    break;
                    case !this.isKeyDown && 40:
                        this.widget.rotateDown();
                        this.updateFloor('down');
                        this.isKeyDown = !this.isKeyDown;
                    break;
                    default:
                        keyState[event['keyCode'] || event['which']] = true;
                    break;
                }
            },
            resetKeyPress(event) {
                var keyState = {};
                switch (event['keyCode']) {
                    case 38:
                        this.isKeyDown = false;
                    break;
                    case 40:
                        this.isKeyDown = false;
                    break;
                    default:
                        keyState[event['keyCode'] || event['which']] = false;
                    break;
                }
            },
            closeHelpPopup() {
                this.hasClicked = true;
                this.close();
            },
            close() {
                this.popupHelpOpen = false;
                if (this.hasClicked) {
                    this.openHelpDialog = false;
                    this.hasClicked = false;
                }
            },
            toggleHelp(){
                if (this.useHelpDialog)
                    this.openHelpDialog = !this.openHelpDialog;
                else {
                    this.popupHelpOpen = !this.popupHelpOpen;
                    if (this.popupHelpOpen)
                        this.updatePopupPosition();
                }
            },
            toggleMinimize() {
                this.$props.options.minimize = !this.$props.options.minimize;
            },
            fullscreenSupported() {
                return Habiteo.hasFullscreenSupport(this.targetElement.parentElement);
            },
            checkNav() {
                if (this.$props.options.gui.autoRotate && this.$props.options.gui.autoRotate.active)
                    this.widget.startRotate(2);
            },
            toggleAutoRotate() {
                this.$props.options.gui.autoRotate.active = this.isRotating = !this.$props.options.gui.autoRotate.active;
                if (this.$props.options.gui.autoRotate.active)
                    this.widget.startRotate(2);
                else
                    this.widget.stopRotate();
            },
            updateFloor(direction) {
                var floorIndex = this.options.floors.findIndex(el => el.active == true);
                if (direction == 'up')
                    floorIndex = (floorIndex + 1) > this.options.floors.length - 1 ? 0 : floorIndex + 1;
                else
                    floorIndex = (floorIndex - 1) < 0 ? this.options.floors.length - 1 : floorIndex - 1;
                EventBus.$emit('updateFloor', this.options.floors[floorIndex]);
            },
            moveLeft() {
                this.widget.rotateLeft();
            },
            startMoveLeft() {
                this.interval = setInterval(() => this.moveLeft(), 300);
            },
            stopMoveLeft() {
                clearInterval(this.interval);
            },
            moveRight() {
                this.widget.rotateRight();
            },
            startMoveRight() {
                this.interval = setInterval(() => this.moveRight(), 300);
            },
            stopMoveRight() {
                clearInterval(this.interval);
            },
            moveUp() {
                this.widget.rotateUp();
            },
            moveDown() {
                this.widget.rotateDown();
            },
            zoomIn() {
                this.widget.zoomIn();
            },
            startZoomIn() {
                this.interval = setInterval(() => this.zoomIn(), 300);
            },
            stopZoomIn() {
                clearInterval(this.interval);
            },
            zoomOut() {
                this.widget.zoomOut();
            },
            startZoomOut() {
                this.interval = setInterval(() => this.zoomOut(), 300);
            },
            stopZoomOut() {
                clearInterval(this.interval);
            },
            toggleFullscreen() {
                Habiteo.toggleFullscreen(document.getElementById('fullScreen'));
                this.fullscreen = !this.fullscreen;
            },
            toggleMouseMode() {
                this.widget.toggleMouseMode();
                this.rotateMode = !this.rotateMode;
            },
            switchResolution(resolution) {
                this.setResolution(resolution);
                EventBus.$emit('updateResolution', resolution);
            },
            updatePopupPosition() {
                if (this.$refs['help-popup'] !== undefined) {
                    let helpPopupWidth = parseInt(this.$refs['help-popup'].styles['width'], 10);
                    let offset = helpPopupWidth / 2;
                    if (this.$refs['help-button'] !== undefined)
                        this.popupPosition = {
                            left: this.$refs['help-button'].$refs.link.offsetLeft - offset,
                            bottom: 95
                        };
                }
            },
            displayInfoPopup() {
                let localStorageKey = 'popup-info-' + this.$route.query.key;
                let nextDisplayDate = this.isLocalstorageAvailable ? localStorage.getItem(localStorageKey) : null;
                let today = new Date().getTime();
                if (!nextDisplayDate || nextDisplayDate <= today) {
                    // 30 days between each time the popup is displayed
                    let lifeCycle = 30 * 24 * 60 * 60 * 1000;
                    let nextDate = new Date(new Date().getTime() + lifeCycle);
                    if (this.isLocalstorageAvailable)
                        localStorage.setItem(localStorageKey, nextDate.getTime());
                    setTimeout(() => {
                            this.toggleHelp();
                    }, 1000);

                }
            },
            setNavigationBarWidth() {
                let sidebar = document.getElementById('sidebar');
                this.screenWidth = window.innerWidth;
                this.navbarWidth = this.screenWidth;
                if (sidebar && this.sidebarOpened)
                    this.navbarWidth -= sidebar.getBoundingClientRect().width;
            },
            hideTooltips() {
                let tooltipKeys = [];
                for (const [key, value] of Object.entries(this.showTooltip)) {
                    if (value == true)
                        tooltipKeys.push(key);
                }

                tooltipKeys.forEach(key => {
                    this.showTooltip[key] = false;
                });
            },
        },
        watch: {
            sidebarOpened() {
                this.setNavigationBarWidth();
            },
            showTooltip: {
                handler() {
                    if (this.tooltipTimeout)
                        window.clearTimeout(this.tooltipTimeout);

                    this.tooltipTimeout = window.setTimeout(() => {
                        this.hideTooltips();
                    }, this.tooltipDuration);
                },
                deep: true
            }
        },
        directives: {
            bind () {
                this.event = event => this.vm.$emit(this.expression, event);
                this.el.addEventListener('click', this.stopProp);
                document.body.addEventListener('click', this.event);
            },
            unbind() {
                this.el.removeEventListener('click', this.stopProp);
                document.body.removeEventListener('click', this.event);
            },
            stopProp(event) {
                event.stopPropagation();
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/components/_threeD-controller.scss";
</style>


// WEBPACK FOOTER //
// src/components/threeDController/ThreeDController.vue